import type { JsonObject } from './contracts';
import { sectionTrackingName, trackedSectionElements } from './section-name';

export const SECTION_ENGAGEMENT_ACTIVITY = 'section_engagement';
export const SECTION_ENGAGEMENT_CHECKPOINT_MS = 15_000;
export const SECTION_ENGAGEMENT_MIN_VISIBLE_PERCENT = 50;
// One scan for a burst of DOM changes. The delay also gives a router time to
// render the next page after history.pushState returns.
const SECTION_RESCAN_DELAY_MS = 200;
// The ingestion contract limits section_engagement values to these ranges.
const MAX_ACTIVE_MS = 24 * 60 * 60 * 1_000;
const MAX_VIEW_COUNT = 1_000;

export type SectionEngagementReason =
  | 'background'
  | 'interval'
  | 'left_view'
  | 'navigation'
  | 'pagehide'
  | 'stop';

export type SectionEngagementHandlers = {
  onActivity(type: typeof SECTION_ENGAGEMENT_ACTIVITY, properties: JsonObject): void;
  onFailure(error: unknown, operation: string): void;
};

// 'remounting': the element was viewable when a new element with the same name
// replaced it. The first viewable report of the new element continues that view.
type SectionVisibility = 'hidden' | 'remounting' | 'viewable';

type ObservedSection = {
  // Null while no element in the document holds the name. The counters stay
  // for the page, so a re-mounted element continues them.
  element: Element | null;
  name: string;
  activeMs: number;
  engagedSince: number | null;
  lastEmittedActiveMs: number;
  lastEmittedMaxVisiblePercent: number;
  lastEmittedViewCount: number;
  maxVisiblePercent: number;
  viewCount: number;
  visibility: SectionVisibility;
};

type SectionObserver = Pick<IntersectionObserver, 'disconnect' | 'observe' | 'unobserve'>;
type SectionMutationObserver = Pick<MutationObserver, 'disconnect' | 'observe'>;

export type SectionEngagementDependencies = {
  clearInterval(intervalId: number): void;
  clearTimeout(timeoutId: number): void;
  createMutationObserver(callback: MutationCallback): SectionMutationObserver;
  createObserver(callback: IntersectionObserverCallback): SectionObserver;
  now(): number;
  setInterval(callback: () => void, intervalMs: number): number;
  setTimeout(callback: () => void, delayMs: number): number;
};

const observerThresholds = Array.from({ length: 21 }, (_, index) => index / 20);

const defaultDependencies = (): SectionEngagementDependencies => ({
  clearInterval: intervalId => window.clearInterval(intervalId),
  clearTimeout: timeoutId => window.clearTimeout(timeoutId),
  createMutationObserver: callback => new MutationObserver(callback),
  createObserver: callback => new IntersectionObserver(callback, { threshold: observerThresholds }),
  now: () => Date.now(),
  setInterval: (callback, intervalMs) => window.setInterval(callback, intervalMs),
  setTimeout: (callback, delayMs) => window.setTimeout(callback, delayMs)
});

const boundedPercent = (value: number): number => Math.min(100, Math.max(0, value));

const containsElement = (nodes: NodeList): boolean => {
  for (const node of nodes) {
    if (node.nodeType === Node.ELEMENT_NODE) return true;
  }
  return false;
};

export const sectionVisiblePercent = (entry: IntersectionObserverEntry): number => {
  if (!entry.isIntersecting) return 0;
  const rootWidth = entry.rootBounds?.width ?? window.innerWidth;
  const rootHeight = entry.rootBounds?.height ?? window.innerHeight;
  const viewableWidth = Math.min(entry.boundingClientRect.width, rootWidth);
  const viewableHeight = Math.min(entry.boundingClientRect.height, rootHeight);
  const viewableArea = viewableWidth * viewableHeight;
  if (viewableArea <= 0) return 0;
  const intersectionArea = entry.intersectionRect.width * entry.intersectionRect.height;
  return boundedPercent(Math.round((intersectionArea / viewableArea) * 100));
};

export class SectionEngagementTracker {
  private readonly dependencies: SectionEngagementDependencies;
  private readonly sections = new Map<string, ObservedSection>();
  private readonly sectionsByElement = new Map<Element, ObservedSection>();
  private documentEngaged: boolean;
  private observer: SectionObserver | null = null;
  private mutationObserver: SectionMutationObserver | null = null;
  private checkpointTimer: number | null = null;
  private rescanTimer: number | null = null;
  private stopped = false;

  constructor(
    private readonly handlers: SectionEngagementHandlers,
    documentEngaged: boolean,
    dependencies: SectionEngagementDependencies = defaultDependencies()
  ) {
    this.documentEngaged = documentEngaged;
    this.dependencies = dependencies;
  }

  start(): void {
    if (this.stopped || this.observer) return;
    try {
      this.observer = this.dependencies.createObserver(entries => this.handleIntersections(entries));
    } catch (error) {
      this.handlers.onFailure(error, 'install_section_observer');
      return;
    }
    this.rescan();
    this.safely('install_section_mutation_observer', () => {
      const mutationObserver = this.dependencies.createMutationObserver(records => this.handleMutations(records));
      mutationObserver.observe(document, { childList: true, subtree: true });
      this.mutationObserver = mutationObserver;
    });
    this.checkpointTimer = this.dependencies.setInterval(
      () => this.safely('capture_section_interval', () => this.checkpoint('interval')),
      SECTION_ENGAGEMENT_CHECKPOINT_MS
    );
  }

  setDocumentEngaged(engaged: boolean): void {
    if (this.stopped || engaged === this.documentEngaged) return;
    const now = this.dependencies.now();
    this.documentEngaged = engaged;
    for (const section of this.sections.values()) {
      if (section.visibility !== 'viewable') continue;
      if (engaged) section.engagedSince = now;
      else {
        this.accrue(section, now);
        this.emit(section, 'background', now);
      }
    }
  }

  resetForNavigation(): void {
    if (this.stopped) return;
    this.finish('navigation');
    this.observer?.disconnect();
    this.sections.clear();
    this.sectionsByElement.clear();
    // The router usually renders the next page after this call. A scan now
    // would observe the elements of the previous page.
    this.cancelRescan();
    this.scheduleRescan();
  }

  stop(reason: 'pagehide' | 'stop'): void {
    if (this.stopped) return;
    this.finish(reason);
    this.stopped = true;
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    this.cancelRescan();
    this.observer?.disconnect();
    this.observer = null;
    if (this.checkpointTimer !== null) this.dependencies.clearInterval(this.checkpointTimer);
    this.checkpointTimer = null;
  }

  private handleMutations(records: readonly MutationRecord[]): void {
    this.safely('capture_section_mutation', () => {
      // Text changes cannot add or remove a section.
      if (records.some(record => containsElement(record.addedNodes) || containsElement(record.removedNodes))) {
        this.scheduleRescan();
      }
    });
  }

  private scheduleRescan(): void {
    if (this.stopped || !this.observer || this.rescanTimer !== null) return;
    this.rescanTimer = this.dependencies.setTimeout(() => {
      this.rescanTimer = null;
      this.rescan();
    }, SECTION_RESCAN_DELAY_MS);
  }

  private cancelRescan(): void {
    if (this.rescanTimer !== null) this.dependencies.clearTimeout(this.rescanTimer);
    this.rescanTimer = null;
  }

  private rescan(): void {
    this.safely('capture_section_scan', () => {
      const observer = this.observer;
      if (this.stopped || !observer) return;
      const now = this.dependencies.now();
      const released = this.releaseDisconnected(observer, now);
      const trackedSections = trackedSectionElements();
      for (const element of trackedSections) {
        if (this.sectionsByElement.has(element)) continue;
        const name = sectionTrackingName(element, trackedSections);
        if (!name) continue;
        const section = this.sections.get(name) ?? this.createSection(name);
        // A name stays unique per page: the element that holds it keeps it.
        if (section.element) continue;
        section.element = element;
        this.sectionsByElement.set(element, section);
        observer.observe(element);
      }
      // A name that did not mount again in this scan has left the page.
      for (const section of released) {
        if (section.element) continue;
        section.visibility = 'hidden';
        this.emit(section, 'left_view', now);
      }
    });
  }

  private releaseDisconnected(observer: SectionObserver, now: number): ObservedSection[] {
    const released: ObservedSection[] = [];
    for (const [element, section] of this.sectionsByElement) {
      if (element.isConnected) continue;
      this.accrue(section, now);
      if (section.visibility === 'viewable') section.visibility = 'remounting';
      observer.unobserve(element);
      this.sectionsByElement.delete(element);
      section.element = null;
      released.push(section);
    }
    return released;
  }

  private createSection(name: string): ObservedSection {
    const section: ObservedSection = {
      element: null,
      name,
      activeMs: 0,
      engagedSince: null,
      lastEmittedActiveMs: 0,
      lastEmittedMaxVisiblePercent: 0,
      lastEmittedViewCount: 0,
      maxVisiblePercent: 0,
      viewCount: 0,
      visibility: 'hidden'
    };
    this.sections.set(name, section);
    return section;
  }

  private handleIntersections(entries: IntersectionObserverEntry[]): void {
    this.safely('capture_section_visibility', () => {
      const now = this.dependencies.now();
      for (const entry of entries) {
        const section = this.sectionsByElement.get(entry.target);
        if (!section) continue;
        const visiblePercent = sectionVisiblePercent(entry);
        section.maxVisiblePercent = Math.max(section.maxVisiblePercent, visiblePercent);
        const previous = section.visibility;
        if (visiblePercent >= SECTION_ENGAGEMENT_MIN_VISIBLE_PERCENT) {
          if (previous === 'viewable') continue;
          section.visibility = 'viewable';
          if (previous === 'hidden') section.viewCount += 1;
          if (this.documentEngaged) section.engagedSince = now;
          continue;
        }
        if (previous === 'hidden') continue;
        section.visibility = 'hidden';
        this.accrue(section, now);
        this.emit(section, 'left_view', now);
      }
    });
  }

  private checkpoint(reason: SectionEngagementReason): void {
    const now = this.dependencies.now();
    for (const section of this.sections.values()) {
      if (section.visibility === 'viewable' && this.documentEngaged) this.accrue(section, now, true);
      this.emit(section, reason, now);
    }
  }

  private finish(reason: SectionEngagementReason): void {
    const now = this.dependencies.now();
    for (const section of this.sections.values()) {
      this.accrue(section, now);
      this.emit(section, reason, now);
      section.visibility = 'hidden';
    }
  }

  private accrue(section: ObservedSection, now: number, continueEngagement = false): void {
    if (section.engagedSince === null) return;
    section.activeMs += Math.max(0, now - section.engagedSince);
    section.engagedSince = continueEngagement ? now : null;
  }

  private emit(section: ObservedSection, reason: SectionEngagementReason, now: number): void {
    if (section.viewCount === 0) return;
    const activeMs = Math.min(MAX_ACTIVE_MS, Math.round(section.activeMs));
    const viewCount = Math.min(MAX_VIEW_COUNT, section.viewCount);
    const changed = activeMs > section.lastEmittedActiveMs
      || section.maxVisiblePercent > section.lastEmittedMaxVisiblePercent
      || viewCount > section.lastEmittedViewCount;
    if (!changed) return;
    this.handlers.onActivity(SECTION_ENGAGEMENT_ACTIVITY, {
      name: section.name,
      activeMs,
      maxVisiblePercent: section.maxVisiblePercent,
      viewCount,
      reason
    });
    section.lastEmittedActiveMs = activeMs;
    section.lastEmittedMaxVisiblePercent = section.maxVisiblePercent;
    section.lastEmittedViewCount = viewCount;
    if (section.visibility === 'viewable' && this.documentEngaged && section.engagedSince === null) {
      section.engagedSince = now;
    }
  }

  private safely(operation: string, callback: () => void): void {
    try {
      callback();
    } catch (error) {
      this.handlers.onFailure(error, operation);
    }
  }
}
